(() => {
  if (window.__omExplicitSizePurchaseV1Initialized) {
    return;
  }

  window.__omExplicitSizePurchaseV1Initialized = true;

  const INFO_SELECTOR = '.product__info-container';
  const PRODUCT_FORM_SELECTOR = 'product-form[data-om-size-required]';
  const SIZE_FIELDSET_SELECTOR = '[data-om-size-option]';
  const SIZE_INPUT_SELECTOR =
    `${SIZE_FIELDSET_SELECTOR} input[type='radio']`;
  const STICKY_VALIDATED_ATTRIBUTE =
    'data-om-sticky-validated';
  const ADD_BUTTON_SELECTOR =
    'button[name="add"], .product-form__submit';
  const BUY_NOW_BUTTON_SELECTOR =
    '.shopify-payment-button__button, ' +
    '.shopify-payment-button button';
  const BUY_NOW_WRAPPER_SELECTOR = '.shopify-payment-button';

  function getProductForm(scope) {
    return scope.querySelector(PRODUCT_FORM_SELECTOR);
  }

  function getAddButton(scope) {
    return scope.querySelector(ADD_BUTTON_SELECTOR);
  }

  function getSizeFieldset(scope) {
    return scope.querySelector(SIZE_FIELDSET_SELECTOR);
  }

  function getVariantIdInputs(scope) {
    const productInfo = scope.closest('product-info') || scope;

    return productInfo.querySelectorAll(
      'form[action*="/cart/add"] input[name="id"], ' +
      'product-form input[name="id"]'
    );
  }

  function getSelectedSizeInput(scope) {
    return scope.querySelector(
      `${SIZE_FIELDSET_SELECTOR} input[type='radio']:checked`
    );
  }

  function requiresSize(scope) {
    return getProductForm(scope)?.dataset.omSizeRequired === 'true';
  }

  function findInputByOptionId(scope, optionId) {
    if (!optionId) {
      return null;
    }

    return Array.from(
      scope.querySelectorAll(SIZE_INPUT_SELECTOR)
    ).find((input) => input.dataset.optionValueId === optionId) || null;
  }

  function clearInitialSizeSelection(scope) {
    scope.querySelectorAll(SIZE_INPUT_SELECTOR).forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
    });

    scope.dataset.omSelectedSizeId = '';
    scope.dataset.omSelectedSizeLabel = '';
  }

  function restoreSelectedSize(scope) {
    const optionId = scope.dataset.omSelectedSizeId;
    const storedInput = findInputByOptionId(scope, optionId);

    if (!storedInput) {
      return null;
    }

    storedInput.checked = true;
    return storedInput;
  }

  function resolveSelectedVariantLocally(scope, sizeInput) {
    if (
      !sizeInput ||
      sizeInput.disabled ||
      sizeInput.dataset.productUrl ||
      sizeInput.dataset.variantAvailable !== 'true' ||
      !sizeInput.dataset.variantId
    ) {
      return false;
    }

    const variantInputs = getVariantIdInputs(scope);

    if (!variantInputs.length) {
      return false;
    }

    variantInputs.forEach((variantInput) => {
      variantInput.value = sizeInput.dataset.variantId;
      variantInput.disabled = false;
    });

    scope.dataset.omVariantPending = 'false';
    scope.dataset.omVariantUnavailable = 'false';
    scope.dataset.omResolvedVariantId = sizeInput.dataset.variantId;

    return true;
  }

  function setButtonDisabled(button, disabled, reason) {
    if (!button) {
      return;
    }

    if (disabled) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.omDisabledReason = reason;
      return;
    }

    button.disabled = false;
    button.removeAttribute('aria-disabled');
    delete button.dataset.omDisabledReason;
  }

  function setAddButtonText(button, text) {
    const textNode = button?.querySelector('span');

    if (textNode && textNode.textContent.trim() !== text) {
      textNode.textContent = text;
    }
  }

  function updateSizeStatus(scope, state) {
    const status = scope.querySelector('[data-om-size-status]');

    if (!status) {
      return;
    }

    status.classList.remove('is-selected', 'is-unavailable');

    if (state === 'needs-size') {
      status.textContent = 'Please select a size to continue.';
      return;
    }

    if (state === 'pending') {
      status.textContent = 'Checking size availability…';
      return;
    }

    if (state === 'sold-out') {
      status.textContent =
        'This size is sold out. Please choose another available size.';
      status.classList.add('is-unavailable');
      return;
    }

    const selectedLabel = scope.dataset.omSelectedSizeLabel || '';
    status.textContent = selectedLabel
      ? `Selected: ${selectedLabel}`
      : 'Size selected.';
    status.classList.add('is-selected');
  }

  function updateBuyNow(scope, state) {
    const disabled = state !== 'available';
    const reason = state === 'needs-size' || state === 'pending'
      ? 'size-selection'
      : 'unavailable';

    scope.querySelectorAll(BUY_NOW_WRAPPER_SELECTOR).forEach((wrapper) => {
      wrapper.classList.toggle(
        'om-buy-now-needs-size',
        state === 'needs-size' || state === 'pending'
      );
      wrapper.classList.toggle(
        'om-buy-now-unavailable',
        state === 'sold-out'
      );
      wrapper.dataset.omBuyNowState = state;
      wrapper.setAttribute(
        'data-om-buy-now-unavailable',
        state === 'sold-out' ? 'true' : 'false'
      );
    });

    scope.querySelectorAll(BUY_NOW_BUTTON_SELECTOR).forEach((button) => {
      setButtonDisabled(button, disabled, reason);

      if (disabled) {
        button.setAttribute('tabindex', '-1');
      } else {
        button.removeAttribute('tabindex');
      }
    });
  }

  function getPurchaseState(scope) {
    if (requiresSize(scope) && !scope.dataset.omSelectedSizeId) {
      return 'needs-size';
    }

    if (scope.dataset.omVariantPending === 'true') {
      return 'pending';
    }

    if (scope.dataset.omVariantUnavailable === 'true') {
      return 'sold-out';
    }

    return 'available';
  }

  function synchronisePurchaseState(scope) {
    if (!scope) {
      return;
    }

    if (scope.dataset.omSelectedSizeId) {
      restoreSelectedSize(scope);
    }

    const state = getPurchaseState(scope);
    const addButton = getAddButton(scope);

    scope.dataset.omPurchaseState = state;
    updateSizeStatus(scope, state);

    if (state === 'available') {
      setButtonDisabled(addButton, false, '');
      setAddButtonText(
        addButton,
        window.variantStrings?.addToCart || 'Add to cart'
      );
    } else if (state === 'needs-size') {
      setButtonDisabled(addButton, true, 'size-selection');
      setAddButtonText(addButton, 'Select a size');
    } else if (state === 'pending') {
      setButtonDisabled(addButton, true, 'size-selection');
      setAddButtonText(addButton, 'Checking availability…');
    } else {
      setButtonDisabled(addButton, true, 'unavailable');
    }

    updateBuyNow(scope, state);
  }

  function mutationAddsRelevantControl(mutation) {
    return Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) {
        return false;
      }

      return (
        node.matches?.(
          `${BUY_NOW_WRAPPER_SELECTOR}, ${SIZE_FIELDSET_SELECTOR}, variant-selects`
        ) ||
        node.querySelector?.(
          `${BUY_NOW_WRAPPER_SELECTOR}, ${SIZE_FIELDSET_SELECTOR}, variant-selects`
        )
      );
    });
  }

  function initialiseProductScope(scope) {
    if (!scope || scope.dataset.omPurchaseControlsInitialised === 'true') {
      return;
    }

    const productForm = getProductForm(scope);

    if (!productForm) {
      return;
    }

    scope.dataset.omPurchaseControlsInitialised = 'true';
    scope.dataset.omVariantPending = 'false';
    scope.dataset.omVariantUnavailable =
      productForm.dataset.omVariantUnavailable === 'true'
        ? 'true'
        : 'false';

    const preserveInitialSize =
      productForm.dataset.omInitialSizeSelected === 'true';
    const initiallyCheckedSize = getSelectedSizeInput(scope);

    if (requiresSize(scope) && !preserveInitialSize) {
      clearInitialSizeSelection(scope);
    } else if (initiallyCheckedSize) {
      scope.dataset.omSelectedSizeId =
        initiallyCheckedSize.dataset.optionValueId || '';
      scope.dataset.omSelectedSizeLabel =
        initiallyCheckedSize.value || '';
    }

    const observer = new MutationObserver((mutations) => {
      if (!mutations.some(mutationAddsRelevantControl)) {
        return;
      }

      window.requestAnimationFrame(() => {
        synchronisePurchaseState(scope);
      });
    });

    observer.observe(scope, {
      childList: true,
      subtree: true
    });

    synchronisePurchaseState(scope);
  }

  function initialiseAllProductScopes(root = document) {
    root.querySelectorAll(INFO_SELECTOR).forEach(initialiseProductScope);
  }

  document.addEventListener(
    'change',
    (event) => {
      const scope = event.target.closest(INFO_SELECTOR);

      if (!scope) {
        return;
      }

      const sizeInput = event.target.closest(SIZE_INPUT_SELECTOR);
      let resolvedLocally = false;

      if (sizeInput) {
        scope.dataset.omSelectedSizeId =
          sizeInput.dataset.optionValueId || '';
        scope.dataset.omSelectedSizeLabel = sizeInput.value || '';
        resolvedLocally = resolveSelectedVariantLocally(scope, sizeInput);
      }

      if (scope.dataset.omSelectedSizeId && !resolvedLocally) {
        scope.dataset.omVariantPending = 'true';
      }

      synchronisePurchaseState(scope);

      if (resolvedLocally) {
        Promise.resolve().then(() => {
          synchronisePurchaseState(scope);
        });
      }
    },
    true
  );

  document.addEventListener('product-info:loaded', (event) => {
    initialiseAllProductScopes(event.target);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAllProductScopes(event.target);
  });

  document.addEventListener(
    'click',
    (event) => {
      const purchaseButton = event.target.closest(
        `${ADD_BUTTON_SELECTOR}, ${BUY_NOW_BUTTON_SELECTOR}`
      );

      if (!purchaseButton) {
        return;
      }

      /*
       * The mobile sticky controller applies this attribute only for the
       * synchronous duration of a purchase click that it has already checked
       * for a selected size, a live variant and an enabled native button.
       * Let that one click reach the existing size-confirmation/product-form
       * pipeline without scrolling the customer to the native controls.
       */
      if (
        purchaseButton.getAttribute(
          STICKY_VALIDATED_ATTRIBUTE
        ) === 'true'
      ) {
        return;
      }

      const scope = purchaseButton.closest(INFO_SELECTOR);

      if (!scope || getPurchaseState(scope) !== 'needs-size') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      getSizeFieldset(scope)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    },
    true
  );

  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS.variantChange
  ) {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const sectionId = String(event.data?.sectionId || '');
      const scope = Array.from(
        document.querySelectorAll(INFO_SELECTOR)
      ).find((candidate) => {
        return candidate.closest('product-info')?.dataset.section === sectionId;
      });

      if (!scope) {
        return;
      }

      const sourceButton = event.data?.html?.getElementById(
        `ProductSubmitButton-${sectionId}`
      );
      const variantUnavailable =
        !event.data?.variant ||
        !sourceButton ||
        sourceButton.dataset.omVariantUnavailable === 'true';

      scope.dataset.omVariantPending = 'false';
      scope.dataset.omVariantUnavailable =
        variantUnavailable ? 'true' : 'false';

      window.requestAnimationFrame(() => {
        synchronisePurchaseState(scope);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseAllProductScopes();
    }, { once: true });
  } else {
    initialiseAllProductScopes();
  }
})();
